import { InfinityIcon, WarningTriangleIcon } from "@xola/icons";
import { Tooltip } from "@xola/ui-kit";
import clsx from "clsx";
import React, { ReactElement, ReactNode } from "react";
import { OPEN_UNLIMITED } from "types";
import { NoTranslate } from "../../Common/NoTranslate";

interface RoasterCountProps {
    total?: number;
    count?: number;

    /** Do we want to show the text *0/* (count) before {total} if it is zero? Defaults to `true` */
    showCountIfZero?: boolean;
}

interface RosterListProps extends RoasterCountProps {
    heading: string;
    icon: ReactElement;
    className?: string;
    children: ReactNode;
    warning?: string | null;
    onClick?: () => void;
}

const RosterCount = ({ total, count, showCountIfZero = true }: RoasterCountProps) => {
    if (total !== undefined && !count) {
        return showCountIfZero ? <span>0/{total}</span> : <span>{total}</span>;
    }

    if (!total || !count) {
        return null;
    }

    const availableCount = count >= OPEN_UNLIMITED ? "99+ " : count;
    if (total >= OPEN_UNLIMITED) {
        return (
            <span>
                <InfinityIcon size="medium" />
            </span>
        );
    }

    return (
        <span>
            {availableCount}/{total}
        </span>
    );
};

export const RosterList = ({
    heading,
    icon,
    count,
    showCountIfZero,
    total,
    className,
    children,
    warning,
    onClick,
}: RosterListProps) => {
    return (
        <div className={clsx(className)} data-testid={`roster-list-${heading}`} onClick={onClick}>
            <h4 className="mb-3 flex items-center space-x-1 text-md font-bold leading-p1 tracking-tightest text-black underline">
                <span>{icon}</span>
                <span>
                    <NoTranslate>
                        <RosterCount total={total} count={count} showCountIfZero={showCountIfZero} />
                    </NoTranslate>
                    &nbsp;{heading}
                </span>
                <span>
                    {warning ? (
                        <Tooltip content={warning}>
                            <span>
                                <WarningTriangleIcon size="medium" className="relative -top-0.5 text-danger" />
                            </span>
                        </Tooltip>
                    ) : null}
                </span>
            </h4>
            <ul className="ml-4 list-outside list-disc space-y-0.5">{children}</ul>
        </div>
    );
};

interface RosterListItemProps {
    className?: string;
    children: ReactNode;
}

export const RosterListItem = ({ className, children }: RosterListItemProps) => {
    return (
        <Tooltip placement="auto" content={<span className="break-all">{children}</span>}>
            <li className={clsx("break-all", className)}>
                <span className="text-gray-darker line-clamp-1">{children}</span>
            </li>
        </Tooltip>
    );
};
